import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const response = await fetch("/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword, confirm_password: confirmPassword }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || "Reset failed. The link may have expired.");
      }
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="launch-root flex min-h-screen items-center justify-center px-4 text-foreground">
        <div className="launch-hero-bg" aria-hidden="true" />
        <div className="aa-glass-card relative z-10 w-full max-w-md rounded-2xl p-8 shadow-xl text-center space-y-4">
          <h1 className="text-xl font-semibold text-foreground">Invalid link</h1>
          <p className="text-sm text-muted-foreground">This reset link is missing or malformed.</p>
          <Link to="/forgot-password" className="text-sm text-primary hover:underline">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="launch-root flex min-h-screen items-center justify-center px-4 text-foreground">
      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="launch-orb launch-orb--one" aria-hidden="true" />
      <div className="launch-orb launch-orb--two" aria-hidden="true" />
      <div className="aa-glass-card relative z-10 w-full max-w-md space-y-6 rounded-2xl p-8 shadow-xl">
        <h1 className="aa-page-title text-2xl font-semibold text-foreground">Choose a new password</h1>

        {success ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Password updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground/80 focus:border-primary/50 focus:outline-none"
                autoComplete="new-password"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground/80 focus:border-primary/50 focus:outline-none"
                autoComplete="new-password"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Min 12 characters · uppercase · lowercase · number · symbol
            </p>

            {error && <p className="text-xs text-rose-400">{error}</p>}

            <Button type="submit" className="w-full uppercase tracking-wide" disabled={loading || !newPassword || !confirmPassword}>
              {loading ? "Updating…" : "Set new password"}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              <Link to="/login" className="hover:underline">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
